import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileDown, ChevronDown } from 'lucide-react';
import './GenerateMenu.css';

const SIGN_GROUP = [
  { type: 'modelovereenkomst',    label: 'Modelovereenkomst' },
  { type: 'modelovereenkomst_hv', label: 'Modelovereenkomst HV (Harbour & Voyage)' },
  { type: 'modelovereenkomst_synergy', label: 'Modelovereenkomst Synergy (Synergy Construction)' },
  { type: 'modelovereenkomst_amsterdam', label: 'Modelovereenkomst Amsterdam (Damen Shiprepair Amsterdam)' },
  { type: 'toets',                label: 'ZZP Toets' },
  { type: 'verklaring',           label: 'Verklaring Arbeidsverleden' },
  { type: 'combined',             label: 'Combined (Model + Toets + Verklaring)', primary: true },
  { type: 'combined_hv',          label: 'Combined HV (Model + Model HV + Toets + Verklaring)', primary: true },
];

const INTERN_GROUP = [
  { type: 'formulier1',           label: 'Formulier 1 — Toetsing Kwalificatie ZZP' },
  { type: 'formulier4',           label: 'Formulier 4 — Toetsing Kwalificatie Opdracht' },
  { type: 'combined_formulieren', label: 'Combined Formulieren (F1 + F4)', primary: true },
];

export default function GenerateMenu({ onGenerate, format = 'pdf', onFormatChange }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const wrapRef = useRef(null);
  const panelRef = useRef(null);

  // Position the floating panel near the trigger button.
  // Flip up if there's not enough room below. Clamp inside the viewport
  // horizontally so neither edge falls off-screen.
  useLayoutEffect(() => {
    if (!open || !wrapRef.current) return;
    const PANEL_MIN_WIDTH = 280;
    const MARGIN = 8;
    const GAP = 6;
    const update = () => {
      const r = wrapRef.current.getBoundingClientRect();
      const panelWidth  = panelRef.current?.offsetWidth  || PANEL_MIN_WIDTH;
      const panelHeight = panelRef.current?.offsetHeight || 400;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // Horizontal: right-align with trigger, then clamp.
      let left = r.right - panelWidth;
      if (left < MARGIN) left = MARGIN;
      const maxLeft = vw - panelWidth - MARGIN;
      if (left > maxLeft) left = maxLeft;

      // Vertical: prefer below; flip above if it would overflow.
      const spaceBelow = vh - r.bottom - MARGIN;
      const spaceAbove = r.top - MARGIN;
      let top;
      if (panelHeight + GAP <= spaceBelow) {
        top = r.bottom + GAP;
      } else if (panelHeight + GAP <= spaceAbove) {
        top = r.top - GAP - panelHeight;
      } else {
        // Neither fits comfortably: pick the side with more room and clamp.
        if (spaceBelow >= spaceAbove) {
          top = Math.max(MARGIN, vh - panelHeight - MARGIN);
        } else {
          top = MARGIN;
        }
      }
      setCoords({ top, left });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (type) => {
    setOpen(false);
    onGenerate(type);
  };

  return (
    <div className="gen-menu-wrap" ref={wrapRef}>
      <button
        type="button"
        className="gen-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <FileDown size={14} />
        Genereren
        <ChevronDown size={14} className={`gen-menu-chev ${open ? 'open' : ''}`} />
      </button>

      {open && createPortal(
        <div
          className="gen-menu-panel"
          role="menu"
          ref={panelRef}
          style={{ top: coords.top, left: coords.left }}
        >
          <div className="gen-menu-format">
            <span className="gen-menu-format-label">Formaat</span>
            <div className="gen-menu-format-toggle">
              <button
                type="button"
                className={format === 'pdf' ? 'active' : ''}
                onClick={() => onFormatChange('pdf')}
              >PDF</button>
              <button
                type="button"
                className={format === 'docx' ? 'active' : ''}
                onClick={() => onFormatChange('docx')}
              >DOCX</button>
            </div>
          </div>

          <div className="gen-menu-section">
            <div className="gen-menu-section-title">Te ondertekenen</div>
            {SIGN_GROUP.map((it) => (
              <button
                key={it.type}
                type="button"
                className={`gen-menu-item ${it.primary ? 'primary' : ''}`}
                onClick={() => pick(it.type)}
                role="menuitem"
              >
                {it.label}
              </button>
            ))}
          </div>

          <div className="gen-menu-section">
            <div className="gen-menu-section-title">Intern (toetsing)</div>
            {INTERN_GROUP.map((it) => (
              <button
                key={it.type}
                type="button"
                className={`gen-menu-item ${it.primary ? 'primary' : ''}`}
                onClick={() => pick(it.type)}
                role="menuitem"
              >
                {it.label}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
